import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { PRACTITIONER_NAME } from "../core/app-meta.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import { I } from "../shared/icons/icons.tsx";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import {
  MESSAGE_CONVERSATIONS,
  MESSAGE_THREADS,
  type ChatMessage,
} from "../messages/conversations.ts";

function hashName(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

/** Pick a conversation deterministically from the patient name, always returns one */
function convForPatient(name: string) {
  const idx = hashName(name) % MESSAGE_CONVERSATIONS.length;
  const conv = MESSAGE_CONVERSATIONS[idx]!;
  return { conv, initialMsgs: MESSAGE_THREADS[conv.id] ?? [] };
}

export function PatientChatWidget() {
  const { current } = Interactions.usePatient();
  const toast = Interactions.useToast();

  // Track messages keyed by patient name to keep independent histories
  const [threadsByPatient, setThreadsByPatient] = React.useState<
    Record<string, ChatMessage[]>
  >({});
  const [draft, setDraft] = React.useState("");
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const { conv, initialMsgs } = React.useMemo(
    () =>
      current ? convForPatient(current.name) : { conv: null, initialMsgs: [] },
    [current?.name],
  );

  const msgs: ChatMessage[] = current
    ? (threadsByPatient[current.name] ?? initialMsgs)
    : [];

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs.length]);

  const sendMessage = () => {
    if (!draft.trim() || !current) return;
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    const newMsg: ChatMessage = { from: "practice", text: draft.trim(), time };
    setThreadsByPatient((prev) => ({
      ...prev,
      [current.name]: [...(prev[current.name] ?? initialMsgs), newMsg],
    }));
    setDraft("");
    toast.push({
      type: "success",
      title: "Message envoyé",
      message: `À ${current.name}`,
    });
  };

  if (!current) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sf-primary-50 text-sf-primary">
          <I.Message size={22} />
        </div>
        <div>
          <p className="text-[13px] font-semibold text-sf-text">
            Aucun patient sélectionné
          </p>
          <p className="mt-1 text-[11.5px] text-sf-text-muted">
            Sélectionnez un patient pour afficher sa conversation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* En-tête patient */}
      <div className="flex shrink-0 items-center gap-2.5 border-b border-sf-border bg-white px-3 py-2">
        <PatientAvatar
          patient={current}
          size={32}
          online={conv?.online ?? false}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-sf-text">
            {current.name}
          </div>
          <div
            className={cn(
              "text-[11px]",
              conv?.online ? "text-sf-success" : "text-sf-text-muted",
            )}
          >
            {conv?.online ? "● En ligne" : "Hors ligne"}
          </div>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-gray-50"
            title="Téléphone"
          >
            <I.Phone size={13} />
          </button>
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded border border-sf-border bg-white text-sf-text-muted hover:bg-gray-50"
            title="Plus"
          >
            <I.Dots size={13} />
          </button>
        </div>
      </div>

      {/* Fil de messages */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-sf-surface-alt px-3 py-3">
        <div className="flex flex-col gap-2">
          {msgs.map((m, i) => {
            const isPractice = m.from === "practice";
            const senderLabel = isPractice ? PRACTITIONER_NAME : current.name;
            const showSender = i === 0 || msgs[i - 1]?.from !== m.from;
            return (
              <div
                key={i}
                className={cn(
                  "flex max-w-[85%] flex-col gap-0.5",
                  isPractice ? "items-end self-end" : "items-start self-start",
                )}
              >
                {showSender && (
                  <span
                    className={cn(
                      "px-1 text-[10.5px] font-semibold leading-none",
                      isPractice ? "text-sf-primary" : "text-sf-text-secondary",
                    )}
                  >
                    {senderLabel}
                  </span>
                )}
                <div
                  className={cn(
                    "px-3 py-2 text-[13px] leading-snug shadow-(--sf-shadow-sm)",
                    isPractice
                      ? "rounded-xl rounded-br-sm bg-sf-primary text-white"
                      : "rounded-xl rounded-bl-sm border border-sf-border bg-white text-sf-text",
                  )}
                >
                  {m.text}
                </div>
                <div
                  className={cn(
                    "px-1 text-[10px] text-sf-text-muted",
                    isPractice && "text-right",
                  )}
                >
                  {m.time}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Zone de saisie */}
      <div className="shrink-0 border-t border-sf-border bg-white px-3 py-2.5">
        <div className="flex items-end gap-2 rounded-lg border border-sf-border bg-sf-surface-alt p-1.5 focus-within:border-sf-primary focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--sf-primary)_12%,transparent)]">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder="Écrire un message…"
            rows={1}
            className="max-h-20 min-h-6 flex-1 resize-none border-none bg-transparent font-[inherit] text-[13px] text-sf-text outline-none"
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={!draft.trim()}
            className={cn(
              "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-sf-primary text-white transition-opacity",
              !draft.trim() && "cursor-not-allowed opacity-40",
            )}
          >
            <I.Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
